/**
 * PercentageInput Component
 *
 * Reusable input component for percentage values with range validation
 *
 * **Important**: This component handles percentage conversion automatically:
 * - Input: Receives decimal values (e.g., 0.07 for 7%)
 * - Display: Shows as percentage (e.g., 7)
 * - Output: Converts back to decimal (e.g., 7 → 0.07)
 *
 * @component
 * @example
 * ```tsx
 * <PercentageInput
 *   name="expectedReturnRate"
 *   value={0.07}  // 7% stored as decimal
 *   onChange={(field, value) => handleChange(field, value)}  // receives 0.07
 *   onBlur={(field, value) => handleBlur(field, value)}  // receives 0.07
 *   label="Expected Return Rate"
 *   min={0}
 *   placeholder="e.g., 7"
 *   error={errors.expectedReturnRate}
 * />
 * ```
 */

import React from "react";
import BigNumber from "bignumber.js";
import { renderHelpText } from "../utils/helpTextRenderer";

export interface PercentageInputProps {
  /** Field name for form handling */
  name: string;
  /** Current numeric value as decimal (e.g., 0.07 for 7%) */
  value?: number | null;
  /** Callback when value changes (optimistic update) - receives decimal value */
  onChange: (name: string, value: number | null) => void;
  /** Callback when input loses focus (triggers save) - receives decimal value */
  onBlur: (name: string, value: number | null) => void;
  /** Label text displayed above input */
  label: string;
  /** Minimum allowed value (typically 0) */
  min: number;
  /** Maximum allowed value (optional, no upper limit if not provided) */
  max?: number;
  /** Optional error message */
  error?: string;
  /** Optional help text displayed below input */
  helpText?: string;
  /** Optional placeholder text (defaults to "0") */
  placeholder?: string;
}

/**
 * PercentageInput - Input field for percentage values
 *
 * Features:
 * - Displays % symbol
 * - Automatically converts between decimal and percentage display
 * - Stores as decimal (0.07), displays as percentage (7)
 * - Validates minimum (typically >= 0)
 * - No maximum limit (simplified constraints)
 * - Supports decimal values (step 0.1)
 * - Converts empty input to 0
 * - Displays error messages
 * - Accessible with proper labels
 *
 * Common ranges:
 * - >= 0: General percentages (no max)
 * - >= 0: Expected return rate (no max)
 * - >= 0: Inflation rate (no max)
 * - >= 0: Withdrawal rate (no max)
 *
 * @param props - Component props
 * @returns Percentage input field
 */
const PercentageInput: React.FC<PercentageInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  min,
  max,
  error,
  helpText,
  placeholder = "0",
}) => {
  // Convert decimal to percentage for display (0.07 → 7) using BigNumber.js for precision
  const displayValue =
    value === null || value === undefined
      ? ""
      : new BigNumber(value).times(100).toNumber();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty input - pass null to indicate cleared field
    if (inputValue === "") {
      onChange(name, null);
      return;
    }

    // Parse as number
    const numericValue = parseFloat(inputValue);

    // Validate it's a number
    /* v8 ignore start -- native number inputs prevent this branch in jsdom/unit tests */
    if (isNaN(numericValue)) {
      return;
    }
    /* v8 ignore stop */

    // Convert percentage to decimal (7 → 0.07) using BigNumber.js for precision
    onChange(name, new BigNumber(numericValue).div(100).toNumber());
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Pass null for empty input to indicate cleared field
    if (inputValue === "") {
      onBlur(name, null);
      return;
    }

    const numericValue = parseFloat(inputValue);

    // Validate it's a number
    /* v8 ignore start -- native number inputs prevent this branch in jsdom/unit tests */
    if (isNaN(numericValue)) {
      onBlur(name, null);
      return;
    }
    /* v8 ignore stop */

    // Convert percentage to decimal (7 → 0.07) using BigNumber.js for precision
    onBlur(name, new BigNumber(numericValue).div(100).toNumber());
  };

  return (
    <div>
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <div className="form-input-wrapper">
        <input
          type="number"
          inputMode="decimal"
          id={name}
          name={name}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onWheel={(e) => e.currentTarget.blur()}
          min={min}
          max={max}
          step="0.1"
          className="percentage-input-full"
          placeholder={placeholder}
          aria-describedby={helpText ? `${name}-help` : undefined}
        />
        <span className="input-suffix-unit">%</span>
      </div>
      {helpText && (
        <p id={`${name}-help`} className="form-help-text">
          {renderHelpText(helpText)}
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default PercentageInput;
